import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import api from '../api';
import { 
  Headphones, MessageSquare, BookOpen, BarChart3, Users, 
  Bot, Send, Plus, RefreshCw, Search, Clock, AlertCircle,
  CheckCircle2, XCircle, ArrowRight, Sparkles, Calendar,
  ThumbsUp, ThumbsDown, Eye, FileText, Settings, Trash2,
  Edit, Phone, Mail, Star, TrendingUp, TrendingDown, Heart
} from 'lucide-react';

const ServiceHubPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [showNewTicketDialog, setShowNewTicketDialog] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general',
    source: 'portal'
  });
  
  // Knowledge Base state
  const [articles, setArticles] = useState([]);
  const [showNewArticleDialog, setShowNewArticleDialog] = useState(false);
  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    category: 'general',
    tags: [],
    status: 'draft',
    is_internal: false
  });
  
  // AI Support state
  const [aiLibrary, setAiLibrary] = useState([]);
  const [showAiChatDialog, setShowAiChatDialog] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showNewLibraryDialog, setShowNewLibraryDialog] = useState(false);
  const [newLibraryEntry, setNewLibraryEntry] = useState({
    topic: '',
    keywords: '',
    question_patterns: '',
    response: '',
    category: 'general',
    escalation_required: false
  });
  
  // Surveys state
  const [surveys, setSurveys] = useState([]);
  
  // Customer Health state
  const [healthScores, setHealthScores] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api.get('/service/analytics/dashboard');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await api.get('/service/tickets');
      setTickets(res.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    }
  }, []);

  const fetchArticles = useCallback(async () => {
    try {
      const res = await api.get('/service/knowledge/articles');
      setArticles(res.data);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    }
  }, []);

  const fetchAiLibrary = useCallback(async () => {
    try {
      const res = await api.get('/service/ai-library');
      setAiLibrary(res.data);
    } catch (error) {
      console.error('Failed to fetch AI library:', error);
    }
  }, []);

  const fetchSurveys = useCallback(async () => {
    try {
      const res = await api.get('/service/surveys');
      setSurveys(res.data);
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
    }
  }, []);

  const fetchHealthScores = useCallback(async () => {
    try {
      const res = await api.get('/service/customer-health');
      setHealthScores(res.data);
    } catch (error) {
      console.error('Failed to fetch health scores:', error);
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchAnalytics(),
        fetchTickets(),
        fetchArticles(),
        fetchAiLibrary(),
        fetchSurveys(),
        fetchHealthScores()
      ]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchAnalytics, fetchTickets, fetchArticles, fetchAiLibrary, fetchSurveys, fetchHealthScores]);

  const handleCreateTicket = async () => {
    try {
      await api.post('/service/tickets', newTicket);
      toast.success('Ticket created successfully');
      setShowNewTicketDialog(false);
      setNewTicket({ subject: '', description: '', priority: 'medium', category: 'general', source: 'portal' });
      fetchTickets();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleCreateArticle = async () => {
    try {
      const articleData = {
        ...newArticle,
        tags: newArticle.tags.length > 0 ? newArticle.tags : []
      };
      await api.post('/service/knowledge/articles', articleData);
      toast.success('Article created successfully');
      setShowNewArticleDialog(false);
      setNewArticle({ title: '', content: '', category: 'general', tags: [], status: 'draft', is_internal: false });
      fetchArticles();
    } catch (error) {
      toast.error('Failed to create article');
    }
  };

  const handleCreateLibraryEntry = async () => {
    try {
      const entryData = {
        ...newLibraryEntry,
        keywords: newLibraryEntry.keywords.split(',').map(k => k.trim()).filter(k => k),
        question_patterns: newLibraryEntry.question_patterns.split('\n').map(p => p.trim()).filter(p => p)
      };
      await api.post('/service/ai-library', entryData);
      toast.success('Library entry created');
      setShowNewLibraryDialog(false);
      setNewLibraryEntry({ topic: '', keywords: '', question_patterns: '', response: '', category: 'general', escalation_required: false });
      fetchAiLibrary();
    } catch (error) {
      toast.error('Failed to create library entry');
    }
  };

  const handleAiQuery = async () => {
    if (!aiQuery.trim()) return;
    try {
      setAiLoading(true);
      const res = await api.post('/service/ai-support/query', { query: aiQuery });
      setAiResponse(res.data);
    } catch (error) {
      toast.error('AI query failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleScheduleMeeting = async () => {
    try {
      // For demo, use a placeholder contact
      const contacts = await api.get('/contacts');
      if (contacts.data.length === 0) {
        toast.error('No contacts available');
        return;
      }
      
      await api.post('/service/ai-support/schedule-meeting', {
        contact_id: contacts.data[0].id,
        topic: aiQuery,
        notes: aiResponse?.response
      });
      toast.success('Meeting scheduled with support agent');
      setShowAiChatDialog(false);
      setAiQuery('');
      setAiResponse(null);
    } catch (error) {
      toast.error('Failed to schedule meeting');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await api.delete(`/service/knowledge/articles/${articleId}`);
      toast.success('Article deleted');
      fetchArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleDeleteLibraryEntry = async (entryId) => {
    if (!window.confirm('Delete this library entry?')) return;
    try {
      await api.delete(`/service/ai-library/${entryId}`);
      toast.success('Entry deleted');
      fetchAiLibrary();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'waiting_customer': return 'bg-purple-100 text-purple-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthColor = (level) => {
    switch (level) {
      case 'healthy': return 'bg-green-100 text-green-800';
      case 'at_risk': return 'bg-yellow-100 text-yellow-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="service-hub-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="service-hub">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Hub</h1>
          <p className="text-gray-500">Customer Support & Success</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAiChatDialog(true)} data-testid="ai-support-btn">
            <Bot className="h-4 w-4 mr-2" />
            AI Support
          </Button>
          <Button onClick={() => setShowNewTicketDialog(true)} data-testid="new-ticket-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Open Tickets</p>
                <p className="text-2xl font-bold">{analytics?.tickets?.open || 0}</p>
                <p className="text-xs text-gray-400">{analytics?.tickets?.total || 0} total</p>
              </div>
              <Headphones className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Resolution</p>
                <p className="text-2xl font-bold">{analytics?.tickets?.avg_resolution_hours || 0}h</p>
                <p className="text-xs text-gray-400">First response: {analytics?.tickets?.avg_first_response_hours || 0}h</p>
              </div>
              <Clock className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Satisfaction</p>
                <p className="text-2xl font-bold">{analytics?.satisfaction?.avg_score || 0}/10</p>
                <p className="text-xs text-gray-400">{analytics?.satisfaction?.total_responses || 0} responses</p>
              </div>
              <Star className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">AI Success Rate</p>
                <p className="text-2xl font-bold">{analytics?.ai_support?.success_rate || 0}%</p>
                <p className="text-xs text-gray-400">{analytics?.ai_support?.total_queries || 0} queries</p>
              </div>
              <Bot className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">
            Help Desk
            {analytics?.tickets?.open > 0 && (
              <Badge className="ml-2 bg-blue-500">{analytics.tickets.open}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="tab-knowledge">Knowledge Base</TabsTrigger>
          <TabsTrigger value="ai-library" data-testid="tab-ai-library">AI Library</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customer Success</TabsTrigger>
          <TabsTrigger value="surveys" data-testid="tab-surveys">Surveys</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Tickets by Priority */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tickets by Priority</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {['urgent', 'high', 'medium', 'low'].map(priority => (
                    <div key={priority} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={getPriorityColor(priority)}>{priority}</Badge>
                      </div>
                      <span className="font-bold">{analytics?.tickets?.by_priority?.[priority] || 0}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Customer Health Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Customer Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4 text-green-500" />
                      <span>Healthy</span>
                    </div>
                    <span className="font-bold text-green-600">{analytics?.customer_health?.healthy || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                      <span>At Risk</span>
                    </div>
                    <span className="font-bold text-yellow-600">{analytics?.customer_health?.at_risk || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-500" />
                      <span>Critical</span>
                    </div>
                    <span className="font-bold text-red-600">{analytics?.customer_health?.critical || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Knowledge Base Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Knowledge Base</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Published Articles</span>
                    <span className="font-bold">{analytics?.knowledge_base?.total_articles || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Total Views</span>
                    <span className="font-bold">{analytics?.knowledge_base?.total_views || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Helpfulness Rate</span>
                    <span className="font-bold">{analytics?.knowledge_base?.helpfulness_rate || 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Support Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">AI Support</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Library Entries</span>
                    <span className="font-bold">{analytics?.ai_support?.library_entries || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Total Queries</span>
                    <span className="font-bold">{analytics?.ai_support?.total_queries || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Escalations</span>
                    <span className="font-bold text-amber-600">{analytics?.ai_support?.escalation_count || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Support Tickets</h3>
            <Button onClick={() => setShowNewTicketDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Ticket
            </Button>
          </div>
          
          <Card>
            <CardContent className="pt-4">
              {tickets.length > 0 ? (
                <ScrollArea className="h-96">
                  <div className="space-y-3">
                    {tickets.map(ticket => (
                      <div key={ticket.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{ticket.ticket_number}</span>
                            <Badge className={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge>
                            <Badge className={getStatusColor(ticket.status)}>{ticket.status?.replace('_', ' ')}</Badge>
                          </div>
                          <p className="text-sm font-medium mt-1">{ticket.subject}</p>
                          <p className="text-xs text-gray-500">
                            {ticket.contact_name || 'Unknown'} • {new Date(ticket.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Headphones className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No tickets yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Knowledge Base Articles</h3>
            <Button onClick={() => setShowNewArticleDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Article
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map(article => (
              <Card key={article.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-base">{article.title}</CardTitle>
                    <Badge className={article.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {article.status}
                    </Badge>
                  </div>
                  <CardDescription>{article.category}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 line-clamp-2">{article.content}</p>
                  <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                    <span><Eye className="h-3 w-3 inline mr-1" />{article.view_count || 0} views</span>
                    <div className="flex items-center gap-2">
                      <span><ThumbsUp className="h-3 w-3 inline" /> {article.helpful_count || 0}</span>
                      <span><ThumbsDown className="h-3 w-3 inline" /> {article.not_helpful_count || 0}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline"><Edit className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteArticle(article.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {articles.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No articles yet</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* AI Library Tab */}
        <TabsContent value="ai-library" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">AI Support Library</h3>
              <p className="text-sm text-gray-500">Controlled responses for AI support bot</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAiChatDialog(true)}>
                <Bot className="h-4 w-4 mr-2" />
                Test AI
              </Button>
              <Button onClick={() => setShowNewLibraryDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Entry
              </Button>
            </div>
          </div>
          
          <Card>
            <CardContent className="pt-4">
              {aiLibrary.length > 0 ? (
                <div className="space-y-3">
                  {aiLibrary.map(entry => (
                    <div key={entry.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{entry.topic}</h4>
                          <Badge>{entry.category}</Badge>
                          {entry.escalation_required && (
                            <Badge className="bg-amber-100 text-amber-800">Escalates</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Used: {entry.usage_count || 0}x</span>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteLibraryEntry(entry.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Keywords: {entry.keywords?.join(', ') || 'None'}</p>
                        <p className="text-sm mt-2 p-2 bg-gray-50 rounded">{entry.response}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Bot className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No library entries yet</p>
                  <p className="text-sm">Add entries to control AI responses</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer Success Tab */}
        <TabsContent value="customers" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Customer Health Scores</h3>
          </div>
          
          <Card>
            <CardContent className="pt-4">
              {healthScores.length > 0 ? (
                <ScrollArea className="h-96">
                  <div className="space-y-3">
                    {healthScores.map(score => (
                      <div key={score.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{score.contact_name}</p>
                          <div className="flex gap-4 mt-1 text-xs text-gray-500">
                            <span>Engagement: {score.engagement_score}</span>
                            <span>Support: {score.support_score}</span>
                            <span>Satisfaction: {score.satisfaction_score}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-2xl font-bold">{score.overall_score}</p>
                            <Badge className={getHealthColor(score.risk_level)}>{score.risk_level}</Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No health scores calculated yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Surveys Tab */}
        <TabsContent value="surveys" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Feedback Surveys</h3>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Survey
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {surveys.map(survey => (
              <Card key={survey.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-base">{survey.name}</CardTitle>
                    <Badge className={survey.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {survey.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <CardDescription>{survey.survey_type?.toUpperCase()}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Responses</span>
                    <span className="font-bold">{survey.response_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-500">Avg Score</span>
                    <span className="font-bold">{survey.avg_score?.toFixed(1) || 'N/A'}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {surveys.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center text-gray-500">
                  <Star className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No surveys yet</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicketDialog} onOpenChange={setShowNewTicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject *</Label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                placeholder="Brief description of the issue"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                placeholder="Detailed description..."
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({...newTicket, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={newTicket.category} onValueChange={(v) => setNewTicket({...newTicket, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="feature">Feature Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTicketDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} disabled={!newTicket.subject || !newTicket.description}>
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Article Dialog */}
      <Dialog open={showNewArticleDialog} onOpenChange={setShowNewArticleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Knowledge Article</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newArticle.title}
                onChange={(e) => setNewArticle({...newArticle, title: e.target.value})}
                placeholder="Article title"
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={newArticle.content}
                onChange={(e) => setNewArticle({...newArticle, content: e.target.value})}
                placeholder="Article content..."
                rows={8}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newArticle.category} onValueChange={(v) => setNewArticle({...newArticle, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="getting-started">Getting Started</SelectItem>
                    <SelectItem value="troubleshooting">Troubleshooting</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="features">Features</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={newArticle.status} onValueChange={(v) => setNewArticle({...newArticle, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewArticleDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateArticle} disabled={!newArticle.title || !newArticle.content}>
              Create Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Library Entry Dialog */}
      <Dialog open={showNewLibraryDialog} onOpenChange={setShowNewLibraryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add AI Library Entry</DialogTitle>
            <DialogDescription>
              Define controlled responses for the AI support bot
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Topic *</Label>
                <Input
                  value={newLibraryEntry.topic}
                  onChange={(e) => setNewLibraryEntry({...newLibraryEntry, topic: e.target.value})}
                  placeholder="e.g., Password Reset"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={newLibraryEntry.category} onValueChange={(v) => setNewLibraryEntry({...newLibraryEntry, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="account">Account</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Keywords (comma-separated)</Label>
              <Input
                value={newLibraryEntry.keywords}
                onChange={(e) => setNewLibraryEntry({...newLibraryEntry, keywords: e.target.value})}
                placeholder="password, reset, forgot, login"
              />
            </div>
            <div>
              <Label>Question Patterns (one per line)</Label>
              <Textarea
                value={newLibraryEntry.question_patterns}
                onChange={(e) => setNewLibraryEntry({...newLibraryEntry, question_patterns: e.target.value})}
                placeholder="How do I reset my password?&#10;I forgot my password&#10;Can't log in"
                rows={3}
              />
            </div>
            <div>
              <Label>Response *</Label>
              <Textarea
                value={newLibraryEntry.response}
                onChange={(e) => setNewLibraryEntry({...newLibraryEntry, response: e.target.value})}
                placeholder="The response AI will give when this topic is matched..."
                rows={4}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="escalation"
                checked={newLibraryEntry.escalation_required}
                onChange={(e) => setNewLibraryEntry({...newLibraryEntry, escalation_required: e.target.checked})}
              />
              <Label htmlFor="escalation">Requires human escalation</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLibraryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateLibraryEntry} disabled={!newLibraryEntry.topic || !newLibraryEntry.response}>
              Add Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Chat Dialog */}
      <Dialog open={showAiChatDialog} onOpenChange={setShowAiChatDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-500" />
              AI Support Assistant
            </DialogTitle>
            <DialogDescription>
              Test the AI support bot or get help with customer queries
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer Query</Label>
              <Textarea
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder="Enter a customer question to test AI response..."
                rows={3}
              />
            </div>
            
            {aiResponse && (
              <div className={`p-4 rounded-lg ${aiResponse.requires_escalation ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {aiResponse.requires_escalation ? (
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  )}
                  <span className="text-sm font-medium">
                    Source: {aiResponse.source} | {aiResponse.requires_escalation ? 'Escalation Needed' : 'Resolved'}
                  </span>
                </div>
                <p className="text-sm">{aiResponse.response}</p>
                
                {aiResponse.follow_up_questions?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-1">Follow-up questions:</p>
                    <ul className="text-sm list-disc list-inside">
                      {aiResponse.follow_up_questions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {aiResponse.suggest_meeting && (
                  <Button className="mt-3" size="sm" onClick={handleScheduleMeeting}>
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule Call with Agent
                  </Button>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAiChatDialog(false);
              setAiQuery('');
              setAiResponse(null);
            }}>
              Close
            </Button>
            <Button onClick={handleAiQuery} disabled={!aiQuery.trim() || aiLoading}>
              {aiLoading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Test Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceHubPage;
